import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Divider,
  FormControlLabel,
  Checkbox,
  Grid,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  OpenInNew as OpenInNewIcon,
  FolderShared as FolderSharedIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import api from '../../services/api';

// eslint-disable-next-line max-lines-per-function
export default function SharedDrivesGlobalSearch({ open, onClose, preSelectedDrives = [] }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [error, setError] = useState(null);
  const [selectedDrives, _setSelectedDrives] = useState(preSelectedDrives);
  const [showFilters, setShowFilters] = useState(false);
  const [fileTypeFilters, setFileTypeFilters] = useState({
    documents: false,
    spreadsheets: false,
    presentations: false,
    pdfs: false,
    images: false,
    videos: false
  });

  const fileTypeMimeTypes = {
    documents: ['application/vnd.google-apps.document', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    spreadsheets: ['application/vnd.google-apps.spreadsheet', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    presentations: ['application/vnd.google-apps.presentation', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'],
    pdfs: ['application/pdf'],
    images: ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'],
    videos: ['video/mp4', 'video/quicktime', 'video/x-msvideo']
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    try {
      setSearching(true);
      setError(null);
      setSearchResults(null);

      // Collect selected file types
      const selectedFileTypes = [];
      Object.entries(fileTypeFilters).forEach(([type, selected]) => {
        if (selected) {
          selectedFileTypes.push(...fileTypeMimeTypes[type]);
        }
      });

      const options = {
        driveIds: selectedDrives.length > 0 ? selectedDrives : undefined,
        fileTypes: selectedFileTypes.length > 0 ? selectedFileTypes : undefined,
        maxResultsPerDrive: 20
      };

      const response = await api.searchAllSharedDrives(searchQuery, options);
      setSearchResults(response);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search drives');
    } finally {
      setSearching(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !searching) {
      handleSearch();
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const getFileIcon = (file) => {
    if (file.isFolder) return <FolderIcon color="action" />;
    return <FileIcon color="action" />;
  };

  const handleOpenFile = (file) => {
    if (file.webViewLink) {
      window.open(file.webViewLink, '_blank');
    }
  };

  // eslint-disable-next-line max-lines-per-function
  const renderSearchResults = () => {
    if (!searchResults) return null;

    const { summary, results } = searchResults;

    return (
      <Box sx={{ mt: 3 }}>
        {/* Summary */}
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="subtitle1">
                Found <strong>{summary.totalResults}</strong> results across{' '}
                <strong>{summary.successfulSearches}</strong> drives
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Chip
                  label={`${summary.successfulSearches} searched`}
                  color="success"
                  size="small"
                  icon={<CheckCircleIcon />}
                />
                {summary.failedSearches > 0 && (
                  <Chip
                    label={`${summary.failedSearches} failed`}
                    color="error"
                    size="small"
                    icon={<ErrorIcon />}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
        {/* Results by Drive */}
        {results.map((driveResult) => (
          <Accordion key={driveResult.driveId} defaultExpanded={driveResult.resultCount > 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                <FolderSharedIcon sx={{ mr: 1 }} color="primary" />
                <Typography sx={{ flexGrow: 1 }}>
                  {driveResult.driveName}
                </Typography>
                {driveResult.status === 'success' ? (
                  <Chip
                    label={`${driveResult.resultCount} results`}
                    size="small"
                    color={driveResult.resultCount > 0 ? 'primary' : 'default'}
                  />
                ) : (
                  <Chip label="Error" size="small" color="error" />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {driveResult.status === 'error' ? (
                <Alert severity="error">{driveResult.error}</Alert>
              ) : driveResult.files.length === 0 ? (
                <Typography color="text.secondary">No results found in this drive</Typography>
              ) : (
                <List dense>
                  {driveResult.files.map((file, fileIndex) => (
                    <React.Fragment key={file.id}>
                      <ListItem
                        button
                        onClick={() => handleOpenFile(file)}
                        sx={{
                          '&:hover': {
                            bgcolor: 'action.hover',
                            '& .MuiListItemSecondaryAction-root': {
                              visibility: 'visible'
                            }
                          }
                        }}
                      >
                        <ListItemIcon>{getFileIcon(file)}</ListItemIcon>
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          secondaryTypographyProps={{ component: 'div' }}
                          primary={file.name}
                          secondary={
                            <Box component="span" sx={{ display: 'flex', gap: 2 }}>
                              <Typography variant="caption" component="span">
                                Modified: {formatDate(file.modifiedTime)}
                              </Typography>
                              {file.size && (
                                <Typography variant="caption" component="span">
                                  Size: {formatFileSize(file.size)}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction sx={{ visibility: 'hidden' }}>
                          <Tooltip title="Open in Google Drive">
                            <IconButton edge="end" size="small" onClick={() => handleOpenFile(file)}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {fileIndex < driveResult.files.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', display: 'flex', flexDirection: 'column' }
      }}
    >
      <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Search All Shared Drives</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
        {/* Search Bar */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search for files and folders across all shared drives..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={searching}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {searchQuery && (
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon />
                      </IconButton>
                    )}
                    <Tooltip title="Filters">
                      <IconButton
                        size="small"
                        onClick={() => setShowFilters(!showFilters)}
                        color={showFilters ? 'primary' : 'default'}
                      >
                        <FilterListIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="contained"
                      onClick={handleSearch}
                      disabled={!searchQuery.trim() || searching}
                      sx={{ ml: 1 }}
                    >
                      Search
                    </Button>
                  </Box>
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* Filters */}
        {showFilters && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>
              File Type Filters
            </Typography>
            <Grid container spacing={1}>
              {Object.entries(fileTypeFilters).map(([type, selected]) => (
                <Grid key={type}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={selected}
                        onChange={(e) => setFileTypeFilters({
                          ...fileTypeFilters,
                          [type]: e.target.checked
                        })}
                      />
                    }
                    label={type.charAt(0).toUpperCase() + type.slice(1)}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {searching && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Searching across shared drives...
            </Typography>
            <LinearProgress sx={{ mt: 2, maxWidth: 400, mx: 'auto' }} />
          </Box>
        )}

        {/* Search Results */}
        {!searching && renderSearchResults()}

        {/* Empty State */}
        {!searching && !searchResults && !error && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <SearchIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Search across all your shared drives
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter a search term to find files and folders
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}